import { cn, NoddiIcon } from 'noddi-ui-common';
import { useState } from 'react';

type PreviewCardProps = {
  src: string;
  name: string;
  size?: number;
  onReplace?: () => void;
  secondary?: React.ReactNode;
  primary?: React.ReactNode;
  tone: 'default' | 'dirty' | 'error';
  readOnly?: boolean;
};

export const PreviewCard = ({
  src,
  name,
  size,
  onReplace,
  secondary,
  primary,
  tone,
  readOnly = false
}: PreviewCardProps) => {
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);

  return (
    <div
      className={cn(
        'flex items-center gap-4 rounded-lg border bg-background p-3 transition-colors',
        tone === 'error' ? 'border-danger' : tone === 'dirty' ? 'border-warning' : 'border-border'
      )}
    >
      {/* Landscape thumbnail: catalog photos and logos are wider than tall, so a square box left most of
          the image as empty checkerboard. `contain` keeps a logo whole; the checker shows what it does not fill. */}
      <div
        className='aspect-3/2 w-32 shrink-0 overflow-hidden rounded-lg border border-outlineStroke'
        style={CHECKER_BACKGROUND}
      >
        <img
          src={src}
          alt=''
          className='size-full object-contain'
          onLoad={(e) => setDims({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
        />
      </div>
      <div className='flex min-w-0 flex-1 flex-col gap-1'>
        <div className='flex items-center gap-2'>
          <div className='min-w-0 truncate text-sm font-medium text-foreground'>{name}</div>
          {tone === 'dirty' && (
            <span className='shrink-0 rounded-full bg-warning50 px-2 py-0.5 text-xs font-medium text-warningDark'>
              Unsaved
            </span>
          )}
        </div>
        <div className='min-h-4 text-xs text-grey'>{formatMeta(dims, size)}</div>
        {!readOnly && onReplace != null && (
          <div className='mt-1 -ml-2 flex flex-wrap items-center gap-1'>
            <ActionButton tone='neutral' icon='RefreshCcw' onClick={onReplace}>
              Replace
            </ActionButton>
            {secondary}
            {primary && <div className='ml-auto'>{primary}</div>}
          </div>
        )}
      </div>
    </div>
  );
};

type ActionButtonProps = {
  tone: 'neutral' | 'danger' | 'muted';
  icon: 'RefreshCcw' | 'Trash2' | 'X';
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
};

const TONE_CLASS: Record<ActionButtonProps['tone'], string> = {
  neutral: 'text-foreground hover:bg-muted',
  danger: 'text-dangerDark hover:bg-danger/5',
  muted: 'text-grey hover:bg-muted'
};

export const ActionButton = ({ tone, icon, onClick, disabled, loading, children }: ActionButtonProps) => (
  <button
    type='button'
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-xs font-medium transition-colors disabled:opacity-50',
      TONE_CLASS[tone]
    )}
  >
    <NoddiIcon name={loading ? 'RefreshCcw' : icon} size='xSmall' className={loading ? 'animate-spin' : undefined} />
    {children}
  </button>
);

const formatMeta = (dims: { w: number; h: number } | null, size?: number): string => {
  const parts: string[] = [];
  if (dims) {
    parts.push(`${dims.w} × ${dims.h}`);
  }
  if (size !== undefined) {
    parts.push(formatBytes(size));
  }
  return parts.join(' · ') || ' ';
};

const formatBytes = (bytes: number): string => {
  if (bytes === 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value % 1 === 0 ? value : value.toFixed(value < 10 ? 1 : 0)} ${units[i]}`;
};

const CHECKER_BACKGROUND: React.CSSProperties = {
  backgroundColor: '#FAFAFA',
  backgroundImage:
    'linear-gradient(45deg, #F0F0F0 25%, transparent 25%),' +
    'linear-gradient(-45deg, #F0F0F0 25%, transparent 25%),' +
    'linear-gradient(45deg, transparent 75%, #F0F0F0 75%),' +
    'linear-gradient(-45deg, transparent 75%, #F0F0F0 75%)',
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px'
};
